import { useCallback } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import {
  NormalFormSelectionField,
  NormalFormDateField,
  NormalFormTextField,
  NormalFormCountrySelectionField,
  NormalAutoCompleteSearchSelectionField,
} from '../molecules';
import { Button } from '../atoms';
import {
  RentChargesReportFilterInput,
  RentChargeStatusOption,
  RentChargeTypeOption,
  OrderDirectionOption,
  PageSizeOption,
  useGetAllStatesLazyQuery,
  useGetAllCitiesLazyQuery,
} from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import {
  rentChargeStatusMapper,
  rentChargeTypeMapper,
  orderDirectionMapper,
  pageSizeMapper,
} from '@/helpers';

type RentChargesFiltersProps = {
  filters: RentChargesReportFilterInput;
  onFilterChange: (newFilters: RentChargesReportFilterInput) => void;
};

const textsData = {
  en: {
    field1: 'Rent Charge Type',
    field2: 'Status',
    field3: 'Order',
    field4: 'Page Size',
    field5: 'Created From',
    field6: 'Created To',
    search: 'Search',
    searchPlaceholder: 'Search by tenant name or description...',
    clearFilters: 'Clear Filters',
    countryLabel: 'Country',
    countryPlaceholder: 'Search country',
    stateLabel: 'State / Province',
    statePlaceholder: 'Search state',
    cityLabel: 'City',
    cityPlaceholder: 'Search city',
  },
  es: {
    field1: 'Tipo de Cargo',
    field2: 'Estado',
    field3: 'Orden',
    field4: 'Registros por página',
    field5: 'Fecha Desde',
    field6: 'Fecha Hasta',
    search: 'Buscar',
    searchPlaceholder: 'Buscar por nombre del tenant o descripción...',
    clearFilters: 'Limpiar Filtros',
    countryLabel: 'País',
    countryPlaceholder: 'Buscar país',
    stateLabel: 'Estado / Provincia',
    statePlaceholder: 'Buscar estado',
    cityLabel: 'Ciudad',
    cityPlaceholder: 'Buscar ciudad',
  },
}

const FiltersWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  margin-bottom: 2rem;
`;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md};

  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

const initialFilters: RentChargesReportFilterInput = {
  createdAtFrom: null,
  createdAtTo: null,
  typeOption: null,
  statusOption: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
  search: null,
  countryId: null,
  stateId: null,
  cityId: null,
};

export const RentChargesFilters = ({ filters, onFilterChange }: RentChargesFiltersProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [getStates, { data: statesData }] = useGetAllStatesLazyQuery();
  const [getCities, { data: citiesData }] = useGetAllCitiesLazyQuery();

  const handleFieldChange = useCallback((field: keyof RentChargesReportFilterInput, value: string | null) => {
    const newFilters: RentChargesReportFilterInput = {
      ...filters,
      [field]: value === '' ? null : value,
    };
    onFilterChange(newFilters);
  }, [filters, onFilterChange]);

  const stateOptions =
    statesData?.states.map((state) => ({
      name: state.name,
      value: state.geonamesAdmin1Code,
    })) ?? [];

  const cityOptions =
    citiesData?.cities.map((city) => ({
      name: city.name,
      value: city.geonamesId,
    })) ?? [];

  const handleCountryChange = useCallback((value: string | null) => {
    const newCountryId = value || null;
    const newFilters: RentChargesReportFilterInput = {
      ...filters,
      countryId: newCountryId,
      stateId: null,
      cityId: null,
    };
    onFilterChange(newFilters);

    if (newCountryId) {
      getStates({ variables: { countryCode: newCountryId } });
    }
  }, [filters, getStates, onFilterChange]);

  const handleStateChange = useCallback((value: string | null) => {
    const newStateId = value === '' ? null : value;
    onFilterChange({
      ...filters,
      stateId: newStateId,
      cityId: null,
    });
    if (newStateId) {
      getCities({ variables: { stateCode: newStateId } });
    }
  }, [filters, getCities, onFilterChange]);

  const handleCityChange = useCallback((value: string | null) => {
    handleFieldChange('cityId', value);
  }, [handleFieldChange]);

  const statusOptions = Object.values(RentChargeStatusOption).map(value => ({
    name: rentChargeStatusMapper(value, userLanguage),
    value: value,
  }));

  const typeOptions = Object.values(RentChargeTypeOption).map(value => ({
    name: rentChargeTypeMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      return dateObj.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleDateChange = (field: 'createdAtFrom' | 'createdAtTo', value: string | null) => {
    const newFilters: RentChargesReportFilterInput = {
      ...filters,
      [field]: value ? new Date(value).toISOString() : null,
    };
    onFilterChange(newFilters);
  };

  return (
    <FiltersWrapper>
      <Row gutterWidth={16} align="center">
        <Col md={4} sm={12}>
          <NormalFormTextField
            label={texts.search}
            name="search"
            placeholder={texts.searchPlaceholder}
            value={filters.search || ''}
            onChange={(value) => handleFieldChange('search', value)}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormDateField
            label={texts.field5}
            name="createdAtFrom"
            value={formatDateForInput(filters.createdAtFrom)}
            onChange={(value) => handleDateChange('createdAtFrom', value)}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormDateField
            label={texts.field6}
            name="createdAtTo"
            value={formatDateForInput(filters.createdAtTo)}
            onChange={(value) => handleDateChange('createdAtTo', value)}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<RentChargeTypeOption>
            label={texts.field1}
            name="type"
            options={typeOptions}
            value={filters.typeOption}
            onChange={(value) => handleFieldChange('typeOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<RentChargeStatusOption>
            label={texts.field2}
            name="status"
            options={statusOptions}
            value={filters.statusOption}
            onChange={(value) => handleFieldChange('statusOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
      </Row>
      <Row gutterWidth={16} align="center">
        <Col md={3} sm={6}>
          <NormalFormCountrySelectionField
            label={texts.countryLabel}
            name="countryId"
            value={filters.countryId ?? undefined}
            onChange={handleCountryChange}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalAutoCompleteSearchSelectionField
            label={texts.stateLabel}
            name="stateId"
            placeholder={texts.statePlaceholder}
            value={filters.stateId ?? undefined}
            onChange={handleStateChange}
            options={stateOptions}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalAutoCompleteSearchSelectionField
            label={texts.cityLabel}
            name="cityId"
            placeholder={texts.cityPlaceholder}
            value={filters.cityId ?? undefined}
            onChange={handleCityChange}
            options={cityOptions}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.field3}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </Col>
        <Col md={1} sm={6}>
          <NormalFormSelectionField<PageSizeOption>
            label={texts.field4}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </Col>
      </Row>
      <Row gutterWidth={16} align="center">
        <ButtonCol md={12} sm={12}>
          <Button secondary onClick={() => onFilterChange(initialFilters)}>
            {texts.clearFilters}
          </Button>
        </ButtonCol> 
      </Row>
    </FiltersWrapper>
  );
};

